import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
import { Link } from "react-router-dom";
import "swiper/css";
import "swiper/css/navigation";

import { useState, useEffect } from "react";
import {
  ChevronLeft,
  ChevronRight,
  Instagram,
  Youtube,
  Facebook,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import HeaderStyle from "../assets/stylesheets/Header.module.css";
import { Logo } from "../assets/logo/Logo";
import { getCategories } from "../api/farm-service/categoryService";
import Logger from "../utils/logger";
import SearchBar from "../components/common/SearchBar";
import { SearchLogo}  from '../assets/logo/Search';
import { ReactComponent as Close} from '../assets/icons/close.svg';

const log = Logger.create('Header');

const TikTok = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path
      d="M16.6 5.82C15.9 5.06 15.5 4.08 15.5 3H12.4V16.94C12.4 18.6797 11.0797 20 9.34 20C7.6003 20 6.28 18.6797 6.28 16.94C6.28 15.2003 7.6003 13.88 9.34 13.88C9.89 13.88 10.41 14.04 10.86 14.3V11.03C10.36 10.92 9.84 10.87 9.32 10.87C6.04 10.87 3.38 13.54 3.38 16.81C3.38 20.08 6.04 22.75 9.32 22.75C12.6 22.75 15.26 20.08 15.26 16.81V9.66C16.45 10.56 17.91 11.09 19.46 11.09V8C19.46 8 17.75 8.11 16.6 5.82Z"
      fill="currentColor"
    />
  </svg>
);


export default function Header() {
  const navigate = useNavigate();

  const [scrollY, setScrollY] = useState(0);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [headerVisible, setHeaderVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [categories, setCategories] = useState([]);
  const [showMobileSearch, setShowMobileSearch] = useState(false);

  // Specific order for certain categories - moved outside the function
  const preferredOrder = {
    'PYO Farm': 1, 
    'Animal Farm': 2,
    'Festivals': 3,
    'Kids Play Area': 4,
    'Farmers Market': 5,
    'Farmstays': 6,
    'Winery': 7,
    'Cidery': 8,
    'Tree Farm': 9,
    'Weddings': 10,
    'Photography': 11,
  };

  useEffect(() => {
    let ticking = false;
    let scrollTimeout;
    
    const onScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          const currentScrollY = window.scrollY;
          
          // Set scrolled state immediately (for header height change)
          setIsScrolled(currentScrollY > 1);
          
          // Determine scroll direction with improved logic
          if (currentScrollY > lastScrollY && currentScrollY > 70) {
            // Only hide header if we're not at the top and scrolling down significantly
            // Add a small delay to prevent flickering
            clearTimeout(scrollTimeout);
            scrollTimeout = setTimeout(() => {
              // Check if we're still scrolling down
              if (window.scrollY >= currentScrollY) {
                setHeaderVisible(false);
              }
            }, 100);
          } else if (currentScrollY < lastScrollY) {
            // Always show header immediately when scrolling up
            setHeaderVisible(true);
          }
          
          setLastScrollY(currentScrollY);
          setScrollY(currentScrollY);
          ticking = false;
        });
        ticking = true;
      }
    };

    const onResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onResize, { passive: true });

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
      clearTimeout(scrollTimeout);
    };
  }, [lastScrollY]);

  // Apply appropriate class to main content container for transform
  useEffect(() => {
    const mainContainer = document.querySelector('main') || document.getElementById('root');
    
    if (mainContainer) {
      if (headerVisible) {
        mainContainer.classList.add(HeaderStyle['header-visible']);
        mainContainer.classList.remove(HeaderStyle['header-hidden']);
      } else {
        // Only add header-hidden if we're actually hidden and past the initial scroll threshold
        if (scrollY > 70) {
          mainContainer.classList.add(HeaderStyle['header-hidden']);
          mainContainer.classList.remove(HeaderStyle['header-visible']);
        }
      }
    }
  }, [headerVisible, scrollY]);
  
  // Function to sort categories in custom order
  const sortCategories = (categories) => {
    return [...categories].sort((a, b) => {
      // Sort by priority field if available
      if (a.priority !== undefined && b.priority !== undefined) {
        return a.priority - b.priority;
      }
      
      // Use predefined order if available, otherwise use 999
      const orderA = preferredOrder[a.name] || 999;
      const orderB = preferredOrder[b.name] || 999;
      
      if (orderA !== orderB) {
        return orderA - orderB;
      }
      
      // Fall back to alphabetical sorting
      return a.name.localeCompare(b.name);
    });
  };

  const handleMobileSearchOpen = () => {
    setShowMobileSearch(true);
    // Prevent body scroll when modal is open
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    
    // Trigger immediate dropdown display and focus
    setTimeout(() => {
      const searchInput = document.querySelector('.mobile-search-container input');
      if (searchInput) {
        // Trigger a focus event to ensure dropdown shows
        searchInput.focus();
        // Dispatch a custom event to force dropdown visibility
        searchInput.dispatchEvent(new Event('focus', { bubbles: true }));
      }
    }, 150);
  };

  const handleMobileSearchClose = () => {
    setShowMobileSearch(false);
    // Restore body scroll
    document.body.style.overflow = '';
    document.body.style.position = '';
    document.body.style.width = '';
  };

  // Close modal when switching to desktop
  useEffect(() => {
    if (!isMobile && showMobileSearch) {
      handleMobileSearchClose();
    }
  }, [isMobile, showMobileSearch]);

  // Get Categories
  useEffect(() => {
    (async () => {
      try {
        const res = await getCategories();
        // Apply the sorting function to the response data
        const customSortedCategories = sortCategories(res.data);
        setCategories(customSortedCategories);
      } catch (err) {
        log.error("Failed to load categories", err);
      }
    })();
  }, []);

  return (
    <>
      <header
        className={`${HeaderStyle.header} ${HeaderStyle["home-header"]} ${
          isScrolled ? HeaderStyle.scrolled : ""
        } ${!headerVisible ? HeaderStyle.hidden : ""}`}
      >
      <div 
        className={`${HeaderStyle["header-container"]} ${HeaderStyle["home-header-container"]}`}
      >
        <div className={HeaderStyle["header-top"]}>
          {!isMobile && (
            <>
              <div className={HeaderStyle["logo-container"]}>
                <a href="/" className={HeaderStyle["logo-link"]}>
                  <Logo
                    className={HeaderStyle["logo"]}
                    aria-label="FarmCiti Logo"
                  />
                </a>
              </div>

              <div className={HeaderStyle["header-search-container"]}>
                <SearchBar
                  placeholder="Search farms"
                  className={HeaderStyle["header-search-bar"]}
                />
              </div>

              <div className={HeaderStyle["social-links"]}>
                <a href="https://www.instagram.com/farmciti.ca" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                  <Instagram size={20} />
                </a>
                <a href="https://www.tiktok.com/@farmciti_canada" target="_blank" rel="noopener noreferrer" aria-label="TikTok">
                  <TikTok size={20} />
                </a>
                <a href="https://www.youtube.com/@FarmCitiCanada" target="_blank" rel="noopener noreferrer" aria-label="YouTube">
                  <Youtube size={20} />
                </a>
                <a href="https://www.facebook.com/people/FarmCiti/61573789681716/" target="_blank" rel="noopener noreferrer" aria-label="Facebook">
                  <Facebook size={20} />
                </a>
              </div>
            </>
          )}
          
          {/* Mobile version - Search Button */}
          {isMobile && (
            <>
              <div className={HeaderStyle["logo-container"]}>
                <a href="/" className={HeaderStyle["logo-link"]}>
                  <Logo
                    className={HeaderStyle["logo"]}
                    aria-label="FarmCiti Logo"
                  />
                </a>
              </div>
              <button 
                className={HeaderStyle["mobile-search-logo"]}
                onClick={handleMobileSearchOpen}
                >
                <SearchLogo/>
              </button>
            </>
          )}
        </div>

        <div 
          className={HeaderStyle["category-slider-container"]}
        >
          <Swiper
            modules={[Navigation]}
            spaceBetween={15}
            navigation={{
              nextEl: `.${HeaderStyle["swiper-button-next"]}`,
              prevEl: `.${HeaderStyle["swiper-button-prev"]}`
            }}
            className={HeaderStyle["category-swiper"]}
            breakpoints={{
              0: { slidesPerView: 5 }, // Mobile
              768: { slidesPerView: 7 } // Tablet or more
            }}
          >
            {categories.map(category => (
              <SwiperSlide key={category.id}>
                <div
                  className={HeaderStyle["category-item"]}
                  onClick={() => navigate(`/category/${category.id}`)}
                >
                  <div
                    className={HeaderStyle["category-icon-container"]}
                    dangerouslySetInnerHTML={{ __html: category.iconSvg }}
                  />
                  <span className={HeaderStyle["category-icon-text"]}>
                    {category.name}
                  </span>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>

          {/* Navigation Arrows */}
          <button
            className={`${HeaderStyle["nav-arrow"]} ${HeaderStyle["swiper-button-prev"]}`}
            aria-label="Previous"
          >
            <ChevronLeft size={24} />
          </button>
          <button
            className={`${HeaderStyle["nav-arrow"]} ${HeaderStyle["swiper-button-next"]}`}
            aria-label="Next"
          >
            <ChevronRight size={24} />
          </button>
        </div>
      </div>
    </header>

    {/* Mobile Search Modal - Moved outside header */}
    {isMobile && showMobileSearch && (
      <div className={HeaderStyle["mobile-search-modal"]}>
          <div className={HeaderStyle["mobile-search-container"]}>
            <SearchBar
              placeholder="Search for farms"
              className={HeaderStyle["mobile-search-bar"]}
              onMobileSearchClose={handleMobileSearchClose}
            />
          </div>
      </div>

    )}
    
    {/* Mobile Navigation - positioned independently at bottom */}
    <div className={HeaderStyle["mobile-header-container"]}>
      <nav className={HeaderStyle["mobile-nav"]}>
        {/* Explore */}
        <Link
          to="/explore"
          className={HeaderStyle["mobile-nav-item"]}
          aria-label="Explore"
        >
          <img
            src="/assets-temp/img/iconamoon_search.png"
            alt="Explore"
            className={HeaderStyle["nav-icon"]}
          />

        </Link>

        {/* Home / Active */}
        <Link
          to="/"
          className={`${HeaderStyle["mobile-nav-item"]} ${HeaderStyle["active"]}`}
          aria-label="Home"
        >
          <img
            src="/assets-temp/img/logo-icon.png"
            alt="Home"
            className={HeaderStyle["nav-icon"]}
          />
        </Link>

        {/* Categories */}
        <Link
          to="/categories"
          className={HeaderStyle["mobile-nav-item"]}
          aria-label="Categories"
        >
          <img
            src="/assets-temp/img/ic_round-menu.png"
            alt="Categories"
            className={HeaderStyle["nav-icon"]}
          />

        </Link>
        </nav>
      </div>
    </>
  );
}
